import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Tag,
  Space,
  Modal,
  Form,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Switch,
  InputNumber,
  Typography,
  Tooltip
} from 'antd';
import {
  TeamOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  CalendarOutlined,
  CrownOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import './SubscribersManagement.css';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const SubscribersManagement = () => {
  const { token } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [stats, setStats] = useState({});

  // Fetch subscribers and stats
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch subscribers for current user
      const subscribersResponse = await axios.get('/api/crm/customers', { headers: authHeaders });
      const customers = Array.isArray(subscribersResponse.data) ? subscribersResponse.data : [];
      
      // Fetch stats
      const statsResponse = await axios.get('/api/crm/stats', { headers: authHeaders });
      setStats(statsResponse.data || {});
      
      setSubscribers(customers);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to load subscribers data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [token, fetchData]);

  // Filter subscribers based on search and segment
  const filteredSubscribers = subscribers.filter(subscriber => {
    const matchesSearch = !searchTerm || 
      subscriber.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSegment = segmentFilter === 'all' || 
      (segmentFilter === 'active_members' && subscriber.is_active_member) ||
      (segmentFilter === 'recent_service' && subscriber.last_service_date) ||
      (segmentFilter === 'open_estimates' && subscriber.has_open_estimate) ||
      (segmentFilter === 'high_ltv' && subscriber.is_high_ltv) ||
      (segmentFilter === 'aged_equipment' && subscriber.equipment_age_years > 10);
    
    return matchesSearch && matchesSegment;
  });

  // Handle create/update subscriber
  const handleSubmit = async (values) => {
    try {
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (editingSubscriber) {
        await axios.put(`/api/crm/customers/${editingSubscriber.id}`, values, { headers: authHeaders });
        message.success('Subscriber updated successfully');
      } else {
        await axios.post('/api/crm/customers', values, { headers: authHeaders });
        message.success('Subscriber created successfully');
      }
      
      setModalVisible(false);
      setEditingSubscriber(null);
      form.resetFields();
      fetchData();
    } catch (error) {
      console.error('Error saving subscriber:', error);
      message.error('Failed to save subscriber');
    }
  };

  // Handle delete subscriber
  const handleDelete = async (id) => {
    try {
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`/api/crm/customers/${id}`, { headers: authHeaders });
      message.success('Subscriber deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      message.error('Failed to delete subscriber');
    }
  };

  // Open edit modal
  const handleEdit = (subscriber) => {
    setEditingSubscriber(subscriber);
    form.setFieldsValue({
      name: subscriber.name,
      email: subscriber.email,
      phone: subscriber.phone,
      address: subscriber.address,
      city: subscriber.city,
      state: subscriber.state,
      zip: subscriber.zip,
      opted_out: subscriber.opted_out,
      is_active_member: subscriber.is_active_member,
      last_service_date: subscriber.last_service_date,
      has_open_estimate: subscriber.has_open_estimate,
      is_high_ltv: subscriber.is_high_ltv,
      equipment_age_years: subscriber.equipment_age_years,
      contract_status: subscriber.contract_status,
      service_frequency: subscriber.service_frequency
    });
    setModalVisible(true);
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <UserOutlined />
          {text || 'N/A'}
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => (
        <Space>
          <MailOutlined />
          {text || 'N/A'}
        </Space>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => (
        <Space>
          <PhoneOutlined />
          {text || 'N/A'}
        </Space>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <Space>
          <HomeOutlined />
          {[record.city, record.state].filter(Boolean).join(', ') || 'N/A'}
        </Space>
      ),
    },
    {
      title: 'Segments',
      key: 'segments',
      render: (_, record) => (
        <Space wrap>
          {record.is_active_member && <Tag color="orange">Active Member</Tag>}
          {record.last_service_date && <Tag color="blue">Recent Service</Tag>}
          {record.has_open_estimate && <Tag color="green">Open Estimate</Tag>}
          {record.is_high_ltv && <Tag color="gold">High LTV</Tag>}
          {record.equipment_age_years > 10 && <Tag color="red">Aged Equipment</Tag>}
          {record.opted_out && <Tag color="default">Opted Out</Tag>}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this subscriber?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="subscribers-management">
      <div className="page-header">
        <Title level={2}>
          <TeamOutlined /> Subscribers Management
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingSubscriber(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          Add Subscriber
        </Button>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="All Email Subscribers"
              value={stats.totalCustomers || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Members"
              value={subscribers.filter(s => s.is_active_member).length}
              prefix={<HomeOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Recent Service"
              value={subscribers.filter(s => s.last_service_date).length}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="High LTV"
              value={subscribers.filter(s => s.is_high_ltv).length}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter by segment"
              value={segmentFilter}
              onChange={setSegmentFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Subscribers</Option>
              <Option value="active_members">Active Members</Option>
              <Option value="recent_service">Recent Service</Option>
              <Option value="open_estimates">Open Estimates</Option>
              <Option value="high_ltv">High LTV</Option>
              <Option value="aged_equipment">Aged Equipment</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Subscribers Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredSubscribers}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} subscribers`,
          }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingSubscriber ? 'Edit Subscriber' : 'Add Subscriber'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingSubscriber(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Please enter name' }]}
              >
                <Input placeholder="Enter full name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Phone" name="phone">
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Address" name="address">
                <Input placeholder="Enter street address" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Form.Item label="City" name="city">
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="State" name="state">
                <Input placeholder="Enter state" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="ZIP Code" name="zip">
                <Input placeholder="Enter ZIP code" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Contract Status" name="contract_status">
                <Select placeholder="Select contract status">
                  <Option value="active">Active</Option>
                  <Option value="expired">Expired</Option>
                  <Option value="none">None</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Service Frequency" name="service_frequency">
                <Select placeholder="Select service frequency">
                  <Option value="regular">Regular</Option>
                  <Option value="occasional">Occasional</Option>
                  <Option value="rare">Rare</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Form.Item label="Equipment Age (years)" name="equipment_age_years">
                <InputNumber
                  min={0}
                  max={50}
                  placeholder="Enter equipment age"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Last Service Date" name="last_service_date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Opted Out" name="opted_out" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={6}>
              <Form.Item name="is_active_member" valuePropName="checked">
                <Switch checkedChildren="Active Member" unCheckedChildren="Not Active" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="has_open_estimate" valuePropName="checked">
                <Switch checkedChildren="Open Estimate" unCheckedChildren="No Estimate" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="is_high_ltv" valuePropName="checked">
                <Switch checkedChildren="High LTV" unCheckedChildren="Regular" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingSubscriber ? 'Update' : 'Create'} Subscriber
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingSubscriber(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscribersManagement;
